import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Badge } from "./ui/badge";
import { toast } from "sonner";
import { 
  BookOpen, 
  Video, 
  FileText, 
  Plus, 
  Edit, 
  Trash2, 
  Eye,
  EyeOff,
  RefreshCw,
  TrendingUp
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const AdminFormazione = () => {
  const [contenuti, setContenuti] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingContenuto, setEditingContenuto] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    titolo: '',
    descrizione_breve: '',
    testo_completo: '',
    tipo: 'articolo',
    categoria: 'diventa_agente',
    visibilita: 'candidati',
    immagine_url: '',
    video_url: '',
    pdf_url: '',
    tags: []
  });

  useEffect(() => {
    loadContenuti();
    loadStats();
  }, []);

  const loadContenuti = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API}/api/formazione/contenuti?pubblicato=true`);
      if (!response.ok) throw new Error('Errore caricamento');
      const data = await response.json();
      setContenuti(data);
    } catch (error) {
      console.error('Error loading contents:', error);
      toast.error('Errore nel caricamento contenuti');
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const response = await fetch(`${API}/api/formazione/stats`);
      if (!response.ok) throw new Error('Errore stats');
      const data = await response.json();
      setStats(data);
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  };

  const handleCreateMockData = async () => {
    try {
      const response = await fetch(`${API}/api/formazione/mock-data`, {
        method: 'POST'
      });
      if (!response.ok) throw new Error('Errore mock data');
      toast.success('Contenuti demo creati!');
      loadContenuti();
      loadStats();
    } catch (error) {
      console.error('Error creating mock data:', error);
      toast.error('Errore nella creazione mock data');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const url = editingContenuto 
        ? `${API}/api/formazione/contenuti/${editingContenuto.id}`
        : `${API}/api/formazione/contenuti`;
      
      const method = editingContenuto ? 'PATCH' : 'POST';
      
      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      
      if (!response.ok) throw new Error('Errore salvataggio');
      
      toast.success(editingContenuto ? 'Contenuto aggiornato!' : 'Contenuto creato!');
      setShowModal(false);
      resetForm();
      loadContenuti();
      loadStats();
    } catch (error) {
      console.error('Error saving content:', error);
      toast.error('Errore nel salvataggio');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Sei sicuro di voler eliminare questo contenuto?')) return;
    
    try {
      const response = await fetch(`${API}/api/formazione/contenuti/${id}`, {
        method: 'DELETE'
      });
      
      if (!response.ok) throw new Error('Errore eliminazione');
      
      toast.success('Contenuto eliminato!');
      loadContenuti();
      loadStats();
    } catch (error) {
      console.error('Error deleting content:', error);
      toast.error('Errore nell\'eliminazione');
    }
  };

  const handleEdit = (contenuto) => {
    setEditingContenuto(contenuto);
    setFormData({
      titolo: contenuto.titolo,
      descrizione_breve: contenuto.descrizione_breve,
      testo_completo: contenuto.testo_completo || '',
      tipo: contenuto.tipo,
      categoria: contenuto.categoria,
      visibilita: contenuto.visibilita,
      immagine_url: contenuto.immagine_url || '',
      video_url: contenuto.video_url || '',
      pdf_url: contenuto.pdf_url || '',
      tags: contenuto.tags || []
    });
    setShowModal(true);
  };

  const resetForm = () => {
    setFormData({
      titolo: '',
      descrizione_breve: '',
      testo_completo: '',
      tipo: 'articolo',
      categoria: 'diventa_agente',
      visibilita: 'candidati',
      immagine_url: '',
      video_url: '',
      pdf_url: '',
      tags: []
    });
    setEditingContenuto(null);
  };

  const getTipoIcon = (tipo) => {
    const icons = {
      articolo: FileText,
      video: Video,
      pdf: BookOpen
    };
    const Icon = icons[tipo] || FileText;
    return <Icon className="w-4 h-4" />;
  };

  const getTipoBadge = (tipo) => {
    const colors = {
      articolo: 'bg-blue-100 text-blue-700',
      video: 'bg-purple-100 text-purple-700',
      pdf: 'bg-green-100 text-green-700'
    };
    return colors[tipo] || colors.articolo;
  };

  const getVisibilitaBadge = (visibilita) => {
    const colors = {
      candidati: 'bg-blue-100 text-blue-700',
      agenzie: 'bg-green-100 text-green-700',
      entrambi: 'bg-purple-100 text-purple-700'
    };
    return colors[visibilita] || colors.candidati;
  };

  return (
    <div className="space-y-6">
      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="border-0 shadow-lg rounded-2xl">
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-blue-600">{stats.total_contenuti}</p>
              <p className="text-sm text-gray-600">Totale</p>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-lg rounded-2xl">
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-green-600">{stats.per_candidati}</p>
              <p className="text-sm text-gray-600">Candidati</p>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-lg rounded-2xl">
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-purple-600">{stats.per_agenzie}</p>
              <p className="text-sm text-gray-600">Agenzie</p>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-lg rounded-2xl">
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-orange-600">
                {stats.articoli + stats.video + stats.pdf}
              </p>
              <p className="text-sm text-gray-600">Pubblicati</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Header & Actions */}
      <Card className="border-0 shadow-lg rounded-2xl">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <BookOpen className="w-5 h-5 text-blue-600" />
              Gestione Contenuti Formativi
            </CardTitle>
            <div className="flex gap-2">
              <Button
                onClick={handleCreateMockData}
                variant="outline"
                size="sm"
                className="rounded-xl"
              >
                Mock Data
              </Button>
              <Button
                onClick={loadContenuti}
                variant="outline"
                size="sm"
                className="rounded-xl"
              >
                <RefreshCw className="w-4 h-4" />
              </Button>
              <Button
                onClick={() => {
                  resetForm();
                  setShowModal(true);
                }}
                className="bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white rounded-xl"
              >
                <Plus className="w-4 h-4 mr-2" />
                Nuovo Contenuto
              </Button>
            </div>
          </div>
        </CardHeader>

        <CardContent>
          {loading ? (
            <div className="text-center py-8">
              <div className="w-8 h-8 border-2 border-blue-500/30 border-t-blue-500 rounded-full animate-spin mx-auto mb-4"></div>
              <p className="text-gray-600">Caricamento...</p>
            </div>
          ) : contenuti.length === 0 ? (
            <div className="text-center py-12">
              <BookOpen className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500 mb-4">Nessun contenuto presente</p>
              <Button onClick={handleCreateMockData} variant="outline">
                Crea contenuti demo
              </Button>
            </div>
          ) : (
            <div className="grid gap-4">
              {contenuti.map((contenuto) => (
                <div
                  key={contenuto.id}
                  className="p-4 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <Badge className={`${getTipoBadge(contenuto.tipo)} border-0`}>
                          {getTipoIcon(contenuto.tipo)}
                          <span className="ml-1">{contenuto.tipo}</span>
                        </Badge>
                        <Badge className={`${getVisibilitaBadge(contenuto.visibilita)} border-0`}>
                          {contenuto.visibilita}
                        </Badge>
                        <Badge variant="outline" className="text-gray-600">
                          <Eye className="w-3 h-3 mr-1" />
                          {contenuto.visualizzazioni || 0}
                        </Badge>
                      </div>
                      <h3 className="font-semibold text-gray-900 mb-1">
                        {contenuto.titolo}
                      </h3>
                      <p className="text-sm text-gray-600 mb-2">
                        {contenuto.descrizione_breve}
                      </p>
                      {contenuto.tags && contenuto.tags.length > 0 && (
                        <div className="flex gap-1 flex-wrap">
                          {contenuto.tags.map((tag, idx) => (
                            <span
                              key={idx}
                              className="text-xs bg-blue-50 text-blue-600 px-2 py-1 rounded"
                            >
                              #{tag}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="flex gap-2 ml-4">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleEdit(contenuto)}
                        className="rounded-xl"
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleDelete(contenuto.id)}
                        className="rounded-xl text-red-600 hover:bg-red-50"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Modal Create/Edit */}
      {showModal && (
        <div className="fixed inset-0 bg-black/30 backdrop-blur-sm flex items-center justify-center p-4 z-50 overflow-y-auto">
          <Card className="max-w-2xl w-full border-0 shadow-2xl rounded-3xl my-8">
            <CardHeader>
              <CardTitle>
                {editingContenuto ? 'Modifica Contenuto' : 'Nuovo Contenuto'}
              </CardTitle>
            </CardHeader>
            
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-gray-700 block mb-2">
                    Titolo *
                  </label>
                  <Input
                    value={formData.titolo}
                    onChange={(e) => setFormData({...formData, titolo: e.target.value})}
                    required
                    className="rounded-xl"
                  />
                </div>
                
                <div>
                  <label className="text-sm font-medium text-gray-700 block mb-2">
                    Descrizione Breve *
                  </label>
                  <Textarea
                    value={formData.descrizione_breve}
                    onChange={(e) => setFormData({...formData, descrizione_breve: e.target.value})}
                    required
                    rows={2}
                    className="rounded-xl resize-none"
                  />
                </div>
                
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="text-sm font-medium text-gray-700 block mb-2">
                      Tipo *
                    </label>
                    <select
                      value={formData.tipo}
                      onChange={(e) => setFormData({...formData, tipo: e.target.value})}
                      className="w-full px-3 py-2 rounded-xl border border-gray-200"
                    >
                      <option value="articolo">Articolo</option>
                      <option value="video">Video</option>
                      <option value="pdf">PDF</option>
                    </select>
                  </div>
                  
                  <div>
                    <label className="text-sm font-medium text-gray-700 block mb-2">
                      Visibilità *
                    </label>
                    <select
                      value={formData.visibilita}
                      onChange={(e) => setFormData({...formData, visibilita: e.target.value})}
                      className="w-full px-3 py-2 rounded-xl border border-gray-200"
                    >
                      <option value="candidati">Candidati</option>
                      <option value="agenzie">Agenzie</option>
                      <option value="entrambi">Entrambi</option>
                    </select>
                  </div>
                  
                  <div>
                    <label className="text-sm font-medium text-gray-700 block mb-2">
                      Categoria *
                    </label>
                    <select
                      value={formData.categoria}
                      onChange={(e) => setFormData({...formData, categoria: e.target.value})}
                      className="w-full px-3 py-2 rounded-xl border border-gray-200"
                    >
                      <optgroup label="Candidati">
                        <option value="diventa_agente">Diventa Agente</option>
                        <option value="mindset_crescita">Mindset & Crescita</option>
                        <option value="marketing_vendita">Marketing & Vendita</option>
                      </optgroup>
                      <optgroup label="Agenzie">
                        <option value="gestione_team">Gestione Team</option>
                        <option value="miglioramento_colloqui">Colloqui</option>
                        <option value="riduzione_turnover">Turnover</option>
                        <option value="motivazione_agenti">Motivazione</option>
                      </optgroup>
                    </select>
                  </div>
                </div>
                
                <div>
                  <label className="text-sm font-medium text-gray-700 block mb-2">
                    Testo Completo
                  </label>
                  <Textarea
                    value={formData.testo_completo}
                    onChange={(e) => setFormData({...formData, testo_completo: e.target.value})}
                    rows={6}
                    className="rounded-xl resize-none"
                    placeholder="Markdown supportato..."
                  />
                </div>
                
                <div>
                  <label className="text-sm font-medium text-gray-700 block mb-2">
                    URL Immagine
                  </label>
                  <Input
                    value={formData.immagine_url}
                    onChange={(e) => setFormData({...formData, immagine_url: e.target.value})}
                    placeholder="https://..."
                    className="rounded-xl"
                  />
                </div>
                
                {formData.tipo === 'video' && (
                  <div>
                    <label className="text-sm font-medium text-gray-700 block mb-2">
                      URL Video (YouTube embed)
                    </label>
                    <Input
                      value={formData.video_url}
                      onChange={(e) => setFormData({...formData, video_url: e.target.value})}
                      placeholder="https://www.youtube.com/embed/..."
                      className="rounded-xl"
                    />
                  </div>
                )}
                
                {formData.tipo === 'pdf' && (
                  <div>
                    <label className="text-sm font-medium text-gray-700 block mb-2">
                      URL PDF
                    </label>
                    <Input
                      value={formData.pdf_url}
                      onChange={(e) => setFormData({...formData, pdf_url: e.target.value})}
                      placeholder="Link al PDF..."
                      className="rounded-xl"
                    />
                  </div>
                )}
                
                <div className="flex gap-3 pt-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      setShowModal(false);
                      resetForm();
                    }}
                    className="flex-1 rounded-xl"
                  >
                    Annulla
                  </Button>
                  <Button
                    type="submit"
                    className="flex-1 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white rounded-xl"
                  >
                    {editingContenuto ? 'Aggiorna' : 'Crea'}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default AdminFormazione;
